import React, { useState, useEffect, useCallback } from 'react'
import Swal from 'sweetalert2'
import type { TenantThemeConfig } from '../types/theme'
import * as authService from '../services/authService'
import { getWithdrawalSettings } from '../services/withdrawalSettingsService'
import './ModernMemberPage.css'

interface AssetConfig {
  CDN_BASE_URL: string
  CSS_PATH: string
  IMG_PATH: string
  FONTS_PATH: string
}

interface ModernMemberPageProps {
  themeConfig: TenantThemeConfig
  assetConfig: AssetConfig
}

/** แถวข้อมูลในกล่อง — label ซ้าย, value หรือปุ่มขวา */
const InfoRow: React.FC<{
  label: string
  value?: React.ReactNode
  action?: React.ReactNode
  muted?: boolean
}> = ({ label, value, action, muted }) => {
  const displayValue = value != null && value !== '' ? value : action ? '–' : value
  const showMuted = muted || displayValue === '–'
  return (
    <div className="htd-member__row">
      <span className="htd-member__row-label">{label}</span>
      <div className="htd-member__row-right">
        {displayValue != null && (
          <span className={`htd-member__row-value${showMuted ? ' htd-member__row-value--muted' : ''}`}>
            {displayValue}
          </span>
        )}
        {action ? <span className="htd-member__row-action">{action}</span> : null}
      </div>
    </div>
  )
}

const AddAction: React.FC<{ onClick: () => void; label?: string }> = ({ onClick, label = 'เพิ่ม' }) => (
  <a
    href="#"
    className="htd-member__action"
    onClick={(e) => {
      e.preventDefault()
      onClick()
    }}
  >
    <span>{label}</span>
    <i className="fas fa-plus" aria-hidden />
  </a>
)

/** รายการธนาคารสำหรับเลือก (รหัส + ชื่อ) */
const BANK_OPTIONS: { code: string; name: string }[] = [
  { code: 'KBANK', name: 'ธนาคารกสิกรไทย' },
  { code: 'SCB', name: 'ธนาคารไทยพาณิชย์' },
  { code: 'BBL', name: 'ธนาคารกรุงเทพ' },
  { code: 'KTB', name: 'ธนาคารกรุงไทย' },
  { code: 'BAY', name: 'ธนาคารกรุงศรีอยุธยา' },
  { code: 'TTB', name: 'ธนาคารทหารไทยธนชาต' },
  { code: 'GSB', name: 'ธนาคารออมสิน' },
  { code: 'TBANK', name: 'ธนาคารธนชาต' },
  { code: 'UOBT', name: 'ธนาคารยูโอบี' },
  { code: 'CIMBT', name: 'ธนาคารซีไอเอ็มบี ไทย' },
]

const hasTextValue = (value?: string | null) => Boolean(String(value || '').trim())
const isUnsetLineId = (value?: string | null) => {
  const normalized = String(value || '').trim().toLowerCase()
  return !normalized || normalized === 'system_default'
}

/** หน้าข้อมูลบัญชี (member) — ดึงข้อมูลจาก API user-info, เพิ่มได้ 1 ครั้งเท่านั้น */
const ModernMemberPage: React.FC<ModernMemberPageProps> = () => {
  const [showEditUserModal, setShowEditUserModal] = useState(false)
  const [editTarget, setEditTarget] = useState<'profile' | 'bank' | 'trueWallet'>('profile')
  const [userInfo, setUserInfo] = useState<authService.UserInfoFull | null>(null)
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)
  // ตั้งค่าต่อ tenant: อนุญาตให้ลูกค้ากรอก PromptPay เองหรือไม่ (default เปิด)
  const [promptPayInputEnabled, setPromptPayInputEnabled] = useState(true)
  // ค่าฟอร์มในโมดัล (ผูกกับข้อมูลจริง)
  const [editName, setEditName] = useState('')
  const [editLine, setEditLine] = useState('')
  const [editBankCode, setEditBankCode] = useState('')
  const [editBankName, setEditBankName] = useState('')
  const [editAccountNumber, setEditAccountNumber] = useState('')
  const [editAccountName, setEditAccountName] = useState('')
  const [editTrueName, setEditTrueName] = useState('')
  const [editTrueMobile, setEditTrueMobile] = useState('')
  const [editTrueId, setEditTrueId] = useState('')
  const [editPromptPay, setEditPromptPay] = useState('')

  /** ไม่ล็อก — ให้เพิ่ม/แก้ไขข้อมูลได้ตลอด (รวมบัญชีธนาคารและ TrueMoney) */
  const isProfileLocked = false

  const loadUserInfo = useCallback(async () => {
    if (!authService.isAuthenticated()) {
      setLoading(false)
      return
    }
    setLoading(true)
    setLoadError(null)
    const data = await authService.fetchUserInfoFull()
    setUserInfo(data)
    setLoading(false)
    if (!data) setLoadError('โหลดข้อมูลไม่สำเร็จ')
  }, [])

  const openChangePasswordDialog = useCallback(() => {
    const getThemedSwalOptions = () => ({
      background: 'transparent',
      color: '#f8fbff',
      confirmButtonColor: 'var(--htd-primary)',
      backdrop: 'rgba(4, 10, 27, 0.82)',
      showCloseButton: true,
      buttonsStyling: false,
      customClass: {
        container: 'htd-member-swal-container',
        popup: 'htd-member-swal-popup',
        title: 'htd-member-swal-title',
        htmlContainer: 'htd-member-swal-html',
        actions: 'htd-member-swal-actions',
        confirmButton: 'htd-member-swal-confirm',
        cancelButton: 'htd-member-swal-cancel',
        closeButton: 'htd-member-swal-close',
        validationMessage: 'htd-member-swal-validation',
      },
    })

    Swal.fire({
      title: 'เปลี่ยนรหัสผ่าน',
      html: `
        <div class="htd-member-swal-form">
          <div class="htd-member-swal-tip">
            <i class="fas fa-lock" aria-hidden="true"></i>
            <div>เพื่อความปลอดภัย กรุณากรอกรหัสผ่านเดิมก่อนตั้งรหัสผ่านใหม่</div>
          </div>
          <div class="htd-member-swal-field">
            <label for="swal-current">รหัสผ่านเดิม</label>
            <input id="swal-current" type="password" placeholder="โปรดใส่รหัสผ่านเดิม" autocomplete="current-password" />
          </div>
          <div class="htd-member-swal-field">
            <label for="swal-new">รหัสผ่านใหม่ (อย่างน้อย 6 ตัวอักษร)</label>
            <input id="swal-new" type="password" placeholder="โปรดใส่รหัสผ่านใหม่" autocomplete="new-password" minlength="6" />
          </div>
          <div class="htd-member-swal-field">
            <label for="swal-confirm">ยืนยันรหัสผ่านใหม่</label>
            <input id="swal-confirm" type="password" placeholder="ยืนยันรหัสผ่านใหม่" autocomplete="new-password" minlength="6" />
          </div>
        </div>
      `,
      showCancelButton: true,
      confirmButtonText: '<i class="fas fa-key me-1"></i> เปลี่ยนรหัสผ่าน',
      cancelButtonText: 'ปิด',
      width: '420px',
      ...getThemedSwalOptions(),
      preConfirm: () => {
        const popup = Swal.getPopup()
        if (!popup) return false
        const current = (popup.querySelector('#swal-current') as HTMLInputElement)?.value?.trim()
        const newPw = (popup.querySelector('#swal-new') as HTMLInputElement)?.value?.trim()
        const confirm = (popup.querySelector('#swal-confirm') as HTMLInputElement)?.value?.trim()
        if (!current) {
          Swal.showValidationMessage('กรุณากรอกรหัสผ่านเดิม')
          return false
        }
        if (!newPw || newPw.length < 6) {
          Swal.showValidationMessage('รหัสผ่านใหม่ต้องมีอย่างน้อย 6 ตัวอักษร')
          return false
        }
        if (newPw !== confirm) {
          Swal.showValidationMessage('รหัสผ่านใหม่กับยืนยันไม่ตรงกัน')
          return false
        }
        return { currentPassword: current, newPassword: newPw }
      },
    }).then(async (result) => {
      if (!result.isConfirmed || !result.value) return
      const { currentPassword, newPassword } = result.value
      const res = await authService.changePassword(currentPassword, newPassword)
      if (res.success) {
        await Swal.fire({
          icon: 'success',
          title: 'เปลี่ยนรหัสผ่านสำเร็จ',
          text: 'กรุณาเข้าสู่ระบบใหม่ด้วยรหัสผ่านใหม่',
          ...getThemedSwalOptions(),
        })
        await authService.logout()
        window.location.hash = '/'
        window.location.reload()
      } else {
        await Swal.fire({
          icon: 'error',
          title: 'เปลี่ยนรหัสผ่านไม่สำเร็จ',
          text: res.error || 'กรุณาลองใหม่อีกครั้ง',
          ...getThemedSwalOptions(),
        })
      }
    })
  }, [])

  useEffect(() => {
    loadUserInfo()
  }, [loadUserInfo])

  useEffect(() => {
    let cancelled = false
    getWithdrawalSettings()
      .then((s) => {
        if (!cancelled) setPromptPayInputEnabled(s.promptPayFeatures?.customerInputEnabled !== false)
      })
      .catch(() => {})
    return () => {
      cancelled = true
    }
  }, [])

  // เมื่อเปิดโมดัล ให้ดึงค่าจาก userInfo มาใส่ฟอร์ม
  useEffect(() => {
    if (showEditUserModal && userInfo) {
      setEditName(userInfo.name || '')
      setEditLine(isUnsetLineId(userInfo.lineAccountId) ? '' : userInfo.lineAccountId || '')
      const bank = userInfo.bankAccount?.[0]
      setEditBankCode(bank?.bankCode || '')
      setEditBankName(bank?.bankName || '')
      setEditAccountNumber(bank?.accountNumber || '')
      setEditAccountName(bank?.accountName || '')
      const tw = userInfo.trueWallet?.[0]
      setEditTrueName(tw?.trueWalletName || '')
      setEditTrueMobile(tw?.trueWalletMobile || '')
      setEditTrueId(tw?.trueWalletId || '')
      setEditPromptPay('')
    }
  }, [showEditUserModal, userInfo])

  const openEditModal = useCallback((target: 'profile' | 'bank' | 'trueWallet') => {
    if (isProfileLocked) {
      Swal.fire({
        icon: 'info',
        title: 'ไม่สามารถแก้ไขได้',
        text: 'คุณได้กรอกข้อมูลสมาชิกไว้แล้ว ไม่สามารถแก้ไขได้ เพื่อความปลอดภัยและป้องกันการขโมยเงิน',
        confirmButtonColor: 'var(--htd-primary)',
      })
      return
    }
    setEditTarget(target)
    setShowEditUserModal(true)
  }, [isProfileLocked])

  const user = authService.getCurrentUser()
  const displayUsername = userInfo?.username ?? user?.username ?? user?.mobile ?? '–'
  const displayMobile = userInfo?.mobile ?? user?.mobile ?? user?.username ?? '–'
  const normalizedLineId = isUnsetLineId(userInfo?.lineAccountId) ? '' : String(userInfo?.lineAccountId || '').trim()
  const primaryBank = userInfo?.bankAccount?.find((a) => a.isActive !== false) ?? userInfo?.bankAccount?.[0]
  const primaryTrueWallet = userInfo?.trueWallet?.[0]
  const canEditName = !hasTextValue(userInfo?.name)
  const canEditLine = !hasTextValue(normalizedLineId)
  const canEditBank = !primaryBank
  const canEditTrueWallet = !primaryTrueWallet
  const allowNameEdit = editTarget === 'profile' && canEditName
  const allowLineEdit = editTarget === 'profile' && canEditLine
  const allowBankEdit = editTarget === 'bank' && canEditBank
  const allowTrueWalletEdit = editTarget === 'trueWallet' && canEditTrueWallet
  const allowPromptPayEdit = promptPayInputEnabled && Boolean(primaryBank) && !primaryBank?.promptPayId
  // แสดงช่อง PromptPay ในส่วนบัญชีธนาคารเสมอเมื่อยังไม่มีค่า (รวมกรณีเพิ่มบัญชีครั้งแรก)
  const showPromptPayInBankModal = promptPayInputEnabled && !primaryBank?.promptPayId
  const promptPayFieldEnabled = allowBankEdit || allowPromptPayEdit
  const hasEditableSection = allowNameEdit || allowLineEdit || allowBankEdit || allowTrueWalletEdit || allowPromptPayEdit

  const handleSubmitEditProfile = useCallback(async () => {
    const name = editName.trim()
    const line = editLine.trim()
    const bankCode = editBankCode.trim()
    const bankName = editBankName.trim() || BANK_OPTIONS.find((b) => b.code === editBankCode)?.name || ''
    const accountNumber = editAccountNumber.replace(/\D/g, '')
    const accountName = editAccountName.trim()
    const trueName = editTrueName.trim()
    const trueMobile = editTrueMobile.replace(/\D/g, '')
    const trueId = editTrueId.trim()

    if (allowNameEdit && !name) {
      Swal.fire({ icon: 'warning', title: 'กรุณากรอกชื่อ-นามสกุล', confirmButtonColor: 'var(--htd-primary)' })
      return
    }
    if (allowBankEdit && (!bankCode || !accountNumber || accountNumber.length < 10 || !accountName)) {
      Swal.fire({
        icon: 'warning',
        title: 'กรุณาระบุบัญชีธนาคาร',
        text: 'เลือกธนาคาร หมายเลขบัญชี (10–12 หลัก) และชื่อบัญชี',
        confirmButtonColor: 'var(--htd-primary)',
      })
      return
    }
    const hasAnyTrueWalletInput = Boolean(trueId || trueName || trueMobile)
    if (allowTrueWalletEdit && hasAnyTrueWalletInput && (!trueName || !trueMobile || !trueId)) {
      Swal.fire({
        icon: 'warning',
        title: 'กรุณากรอกข้อมูล TrueMoney ให้ครบ',
        text: 'ระบุชื่อ เบอร์มือถือ 10 หลัก และ Wallet ID ให้ครบถ้วน',
        confirmButtonColor: 'var(--htd-primary)',
      })
      return
    }
    if (allowTrueWalletEdit && trueMobile && trueMobile.length !== 10) {
      Swal.fire({ icon: 'warning', title: 'เบอร์ TrueMoney ต้อง 10 หลัก', confirmButtonColor: 'var(--htd-primary)' })
      return
    }

    const promptPayDigits = editPromptPay.replace(/\D/g, '')
    const wantsPromptPay = promptPayFieldEnabled && promptPayDigits.length > 0 && !primaryBank?.promptPayId
    if (wantsPromptPay && !/^(\d{10}|\d{13})$/.test(promptPayDigits)) {
      Swal.fire({
        icon: 'warning',
        title: 'PromptPay ไม่ถูกต้อง',
        text: 'กรอกเบอร์มือถือ 10 หลัก หรือเลขบัตรประชาชน 13 หลัก',
        confirmButtonColor: 'var(--htd-primary)',
      })
      return
    }

    setSubmitting(true)
    const payload: Parameters<typeof authService.updateUserInfoFull>[0] = {}
    if (allowNameEdit && name) payload.name = name
    if (allowLineEdit && line) payload.lineAccountId = line
    if (allowBankEdit) {
      payload.bankAccount = [{ bankCode, bankName: bankName || bankCode, accountNumber, accountName }]
    }
    if (allowTrueWalletEdit && hasAnyTrueWalletInput) {
      payload.trueWallet = [
        {
          trueWalletId: trueId,
          trueWalletName: trueName,
          trueWalletMobile: trueMobile,
        },
      ]
    }
    if (Object.keys(payload).length === 0 && !wantsPromptPay) {
      setSubmitting(false)
      await Swal.fire({
        icon: 'info',
        title: 'ยังไม่มีข้อมูลใหม่สำหรับบันทึก',
        text: 'กรอกเฉพาะช่องที่ยังว่างก่อนกดบันทึก',
        confirmButtonColor: 'var(--htd-primary)',
      })
      return
    }

    if (Object.keys(payload).length === 0 && wantsPromptPay) {
      const ppRes = await authService.setBankPromptPay(promptPayDigits)
      setSubmitting(false)
      if (ppRes.success) {
        setShowEditUserModal(false)
        setEditPromptPay('')
        await Swal.fire({ icon: 'success', title: 'บันทึก PromptPay สำเร็จ', confirmButtonColor: 'var(--htd-primary)' })
        loadUserInfo()
      } else {
        await Swal.fire({ icon: 'error', title: 'บันทึก PromptPay ไม่สำเร็จ', text: ppRes.error, confirmButtonColor: 'var(--htd-primary)' })
      }
      return
    }

    const res = await authService.updateUserInfoFull(payload)
    if (res.success && wantsPromptPay) {
      const ppRes = await authService.setBankPromptPay(promptPayDigits)
      setSubmitting(false)
      if (ppRes.success) {
        setShowEditUserModal(false)
        setEditPromptPay('')
        await Swal.fire({ icon: 'success', title: 'บันทึกข้อมูลสำเร็จ', confirmButtonColor: 'var(--htd-primary)' })
        loadUserInfo()
      } else {
        await Swal.fire({
          icon: 'warning',
          title: 'บันทึกบัญชีธนาคารแล้ว แต่ PromptPay ไม่สำเร็จ',
          text: ppRes.error || 'กรุณาลองเพิ่ม PromptPay อีกครั้งจากหน้าข้อมูลสมาชิก',
          confirmButtonColor: 'var(--htd-primary)',
        })
        loadUserInfo()
      }
      return
    }

    setSubmitting(false)
    if (res.success) {
      setShowEditUserModal(false)
      await Swal.fire({ icon: 'success', title: 'บันทึกข้อมูลสำเร็จ', confirmButtonColor: 'var(--htd-primary)' })
      loadUserInfo()
    } else {
      await Swal.fire({ icon: 'error', title: 'บันทึกไม่สำเร็จ', text: res.error, confirmButtonColor: 'var(--htd-primary)' })
    }
  }, [editName, editLine, editBankCode, editBankName, editAccountNumber, editAccountName, editTrueName, editTrueMobile, editTrueId, editPromptPay, loadUserInfo, allowNameEdit, allowLineEdit, allowBankEdit, allowTrueWalletEdit, promptPayFieldEnabled, primaryBank?.promptPayId])

  // เพิ่ม PromptPay ลงบัญชีธนาคารหลัก (เซ็ตได้ครั้งเดียว)
  const handleAddPromptPay = useCallback(async () => {
    const { value, isConfirmed } = await Swal.fire({
      icon: 'info',
      title: 'เพิ่ม PromptPay',
      input: 'text',
      inputLabel: 'เบอร์พร้อมเพย์ 10 หลัก หรือเลขบัตรประชาชน 13 หลัก',
      inputPlaceholder: 'กรอกเลขพร้อมเพย์',
      inputAttributes: { inputmode: 'numeric', maxlength: '13' },
      showCancelButton: true,
      confirmButtonText: 'บันทึก',
      cancelButtonText: 'ยกเลิก',
      confirmButtonColor: 'var(--htd-primary)',
      inputValidator: (v: string) => {
        const digits = String(v || '').replace(/\D/g, '')
        return /^(\d{10}|\d{13})$/.test(digits) ? undefined : 'กรอกเบอร์ 10 หลัก หรือเลขบัตรประชาชน 13 หลัก'
      },
    })
    if (!isConfirmed) return
    const digits = String(value || '').replace(/\D/g, '')
    setSubmitting(true)
    const res = await authService.setBankPromptPay(digits)
    setSubmitting(false)
    if (res.success) {
      await Swal.fire({ icon: 'success', title: 'บันทึก PromptPay สำเร็จ', confirmButtonColor: 'var(--htd-primary)' })
      loadUserInfo()
    } else {
      await Swal.fire({ icon: 'error', title: 'บันทึกไม่สำเร็จ', text: res.error, confirmButtonColor: 'var(--htd-primary)' })
    }
  }, [loadUserInfo])


  if (loading) {
    return (
      <div className="content htd-member">
        <div className="htd-member__glow" aria-hidden />
        <div className="htd-member__state">กำลังโหลดข้อมูล...</div>
      </div>
    )
  }

  const displayName = userInfo?.name || displayUsername
  const initial = String(displayName || 'M').charAt(0).toUpperCase()

  return (
    <div className="content htd-member">
      <div className="htd-member__glow" aria-hidden />
      <div className="htd-member__inner">
        <header className="htd-member__hero htd-member-anim htd-member-anim--1">
          <div className="htd-member__badge" aria-hidden>
            {initial}
          </div>
          <div>
            <p className="htd-member__eyebrow">Account Profile</p>
            <h1 className="htd-member__title">ข้อมูลบัญชี</h1>
            <p className="htd-member__desc">
              จัดการข้อมูลเข้าสู่ระบบ บัญชีธนาคาร และ TrueMoney ของคุณ
            </p>
          </div>
        </header>

        {loadError ? (
          <div className="htd-member__alert htd-member-anim htd-member-anim--2" role="alert">
            <span>{loadError}</span>
            <button type="button" className="htd-member__alert-btn" onClick={loadUserInfo}>
              โหลดใหม่
            </button>
          </div>
        ) : null}

        <section className="htd-member__panel htd-member-anim htd-member-anim--2" aria-label="ชื่อผู้ใช้และรหัสผ่าน">
          <div className="htd-member__panel-head">
            <span className="htd-member__panel-icon" aria-hidden>
              <i className="fas fa-user-lock" />
            </span>
            <div>
              <h2>ชื่อผู้ใช้และรหัสผ่าน</h2>
              <p>ข้อมูลสำหรับเข้าสู่ระบบ</p>
            </div>
          </div>
          <div className="htd-member__rows">
            <InfoRow label="เบอร์ที่สมัคร" value={displayMobile} />
            <InfoRow label="Username" value={displayUsername} />
            <InfoRow
              label="Password"
              value="***********"
              muted
              action={
                <a
                  href="#"
                  className="htd-member__action htd-member__action--ghost"
                  onClick={(e) => {
                    e.preventDefault()
                    openChangePasswordDialog()
                  }}
                >
                  <i className="fas fa-key" aria-hidden />
                  <span>เปลี่ยนรหัสผ่าน</span>
                </a>
              }
            />
          </div>
        </section>

        <section className="htd-member__panel htd-member-anim htd-member-anim--3" aria-label="ข้อมูลผู้ใช้">
          <div className="htd-member__panel-head">
            <span className="htd-member__panel-icon" aria-hidden>
              <i className="fas fa-id-card" />
            </span>
            <div>
              <h2>ข้อมูลผู้ใช้</h2>
              <p>โปรไฟล์ บัญชีธนาคาร และวอลเล็ต</p>
            </div>
          </div>
          <div className="htd-member__rows">
            <InfoRow
              label="ชื่อ-นามสกุล"
              value={userInfo?.name || '–'}
              muted={!userInfo?.name}
              action={
                canEditName ? (
                  <AddAction onClick={() => openEditModal('profile')} />
                ) : undefined
              }
            />
            <InfoRow
              label="Line"
              value={normalizedLineId || '–'}
              muted={!normalizedLineId}
              action={
                canEditLine ? <AddAction onClick={() => openEditModal('profile')} /> : undefined
              }
            />
            <InfoRow
              label="หมายเลขบัญชี"
              value={primaryBank?.accountNumber || undefined}
              muted={!primaryBank?.accountNumber}
              action={
                !primaryBank ? <AddAction onClick={() => openEditModal('bank')} /> : undefined
              }
            />
            <InfoRow label="ธนาคาร" value={primaryBank?.bankName || '–'} muted={!primaryBank?.bankName} />
            {(promptPayInputEnabled || primaryBank?.promptPayId) && (
              <InfoRow
                label="PromptPay"
                value={primaryBank?.promptPayId || undefined}
                muted={!primaryBank?.promptPayId}
                action={
                  promptPayInputEnabled && primaryBank && !primaryBank.promptPayId ? (
                    <AddAction onClick={() => void handleAddPromptPay()} />
                  ) : undefined
                }
              />
            )}
            <InfoRow
              label="TrueMoney (ชื่อ)"
              value={primaryTrueWallet?.trueWalletName || undefined}
              muted={!primaryTrueWallet?.trueWalletName}
              action={
                !primaryTrueWallet ? (
                  <AddAction onClick={() => openEditModal('trueWallet')} />
                ) : undefined
              }
            />
            <InfoRow
              label="TrueMoney (เบอร์)"
              value={primaryTrueWallet?.trueWalletMobile || '–'}
              muted={!primaryTrueWallet?.trueWalletMobile}
            />
            <InfoRow
              label="TrueMoney (ID)"
              value={primaryTrueWallet?.trueWalletId || '–'}
              muted={!primaryTrueWallet?.trueWalletId}
            />
          </div>
        </section>
      </div>

      {showEditUserModal && (
        <>
          <div
            className="htd-member-modal-backdrop"
            onClick={() => setShowEditUserModal(false)}
            aria-hidden="true"
          />
          <div className="htd-member-modal" role="dialog" aria-modal="true" aria-labelledby="edituserModalLabel">
            <div className="htd-member-modal__dialog">
              <div className="htd-member-modal__content">
                <div className="htd-member-modal__header">
                  <div>
                    <div className="htd-member-modal__chip">
                      <i className="fas fa-user-edit" aria-hidden />
                      อัปเดตข้อมูลสมาชิก
                    </div>
                    <h5 id="edituserModalLabel">แก้ไขข้อมูลสมาชิก</h5>
                    <p>ช่องที่มีข้อมูลอยู่แล้วจะถูกล็อก และบันทึกเฉพาะส่วนที่ยังว่างเท่านั้น</p>
                  </div>
                  <button
                    type="button"
                    className="htd-member-modal__close"
                    aria-label="ปิด"
                    onClick={() => setShowEditUserModal(false)}
                  >
                    <i className="fas fa-times" aria-hidden />
                  </button>
                </div>

                <div className="htd-member-modal__body">
                  <div className="htd-member-modal__section">
                    <div className="htd-member-modal__section-head">
                      <div>
                        <h6>ข้อมูลพื้นฐาน</h6>
                        <p>ชื่อ-นามสกุลและ Line ID</p>
                      </div>
                      <span className={`htd-member-modal__status${allowNameEdit || allowLineEdit ? ' is-open' : ''}`}>
                        {allowNameEdit || allowLineEdit
                          ? 'เพิ่มข้อมูลได้'
                          : canEditName || canEditLine
                            ? 'เพิ่มได้จากส่วนข้อมูลผู้ใช้'
                            : 'มีข้อมูลแล้ว'}
                      </span>
                    </div>
                    <div className="htd-member-modal__field">
                      <label>ชื่อ-นามสกุล</label>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="ระบุชื่อ-นามสกุลให้ถูกต้อง"
                        value={editName}
                        disabled={!allowNameEdit}
                        readOnly={!allowNameEdit}
                        onChange={(e) => setEditName(e.target.value)}
                      />
                      {!allowNameEdit && (
                        <div className="htd-member-modal__hint">
                          {canEditName
                            ? 'รอบนี้ไม่ได้เปิดแก้ส่วนข้อมูลพื้นฐาน ให้เพิ่มจากปุ่มของส่วนนั้นโดยตรง'
                            : 'ข้อมูลนี้มีอยู่แล้ว จึงไม่สามารถแก้ไขได้'}
                        </div>
                      )}
                    </div>
                    <div className="htd-member-modal__field">
                      <label>Line ID</label>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="ระบุ Line ID"
                        value={editLine}
                        disabled={!allowLineEdit}
                        readOnly={!allowLineEdit}
                        onChange={(e) => setEditLine(e.target.value)}
                      />
                      {!allowLineEdit && (
                        <div className="htd-member-modal__hint">
                          {canEditLine
                            ? 'รอบนี้ไม่ได้เปิดเพิ่ม Line ID เพื่อป้องกันการแก้หลายส่วนพร้อมกัน'
                            : 'มี Line ID อยู่แล้ว ระบบจะเก็บข้อมูลเดิมไว้'}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="htd-member-modal__section">
                    <div className="htd-member-modal__section-head">
                      <div>
                        <h6>บัญชีธนาคาร</h6>
                        <p>ใช้สำหรับรับเงินและยืนยันตัวตนทางการเงิน</p>
                      </div>
                      <span className={`htd-member-modal__status${allowBankEdit ? ' is-open' : ''}`}>
                        {allowBankEdit
                          ? 'เพิ่มข้อมูลได้'
                          : canEditBank
                            ? 'เพิ่มได้จากปุ่มบัญชีธนาคาร'
                            : 'มีข้อมูลแล้ว'}
                      </span>
                    </div>
                    <div className="htd-member-modal__grid">
                      <div className="htd-member-modal__field">
                        <label>ธนาคาร</label>
                        <select
                          className="form-select"
                          value={editBankCode}
                          disabled={!allowBankEdit}
                          onChange={(e) => {
                            const code = e.target.value
                            setEditBankCode(code)
                            const opt = BANK_OPTIONS.find((b) => b.code === code)
                            if (opt) setEditBankName(opt.name)
                          }}
                        >
                          <option value="">เลือกธนาคาร</option>
                          {BANK_OPTIONS.map((b) => (
                            <option key={b.code} value={b.code}>
                              {b.name}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="htd-member-modal__field">
                        <label>เลขบัญชี</label>
                        <input
                          type="text"
                          className="form-control"
                          placeholder="10-12 หลัก"
                          value={editAccountNumber}
                          disabled={!allowBankEdit}
                          readOnly={!allowBankEdit}
                          onChange={(e) => setEditAccountNumber(e.target.value.replace(/\D/g, '').slice(0, 12))}
                        />
                      </div>
                    </div>
                    <div className="htd-member-modal__field">
                      <label>ชื่อบัญชี</label>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="ชื่อบนบัญชีธนาคาร"
                        value={editAccountName}
                        disabled={!allowBankEdit}
                        readOnly={!allowBankEdit}
                        onChange={(e) => setEditAccountName(e.target.value)}
                      />
                      {!allowBankEdit && (
                        <div className="htd-member-modal__hint">
                          {canEditBank
                            ? 'รอบนี้ไม่ได้เปิดเพิ่มบัญชีธนาคาร ให้ใช้ปุ่มเพิ่มของส่วนบัญชีธนาคารโดยตรง'
                            : 'ระบบล็อกข้อมูลบัญชีธนาคารที่มีอยู่แล้วไว้ให้เพื่อความปลอดภัย'}
                        </div>
                      )}
                    </div>
                    {primaryBank?.promptPayId ? (
                      <div className="htd-member-modal__field" style={{ marginTop: '0.85rem' }}>
                        <label>PromptPay</label>
                        <input
                          type="text"
                          className="form-control"
                          value={primaryBank.promptPayId}
                          readOnly
                          disabled
                        />
                        <div className="htd-member-modal__hint">
                          ตั้งค่าแล้ว หากต้องการแก้ไขกรุณาติดต่อเจ้าหน้าที่
                        </div>
                      </div>
                    ) : showPromptPayInBankModal ? (
                      <div className="htd-member-modal__field" style={{ marginTop: '0.85rem' }}>
                        <label>PromptPay (ไม่บังคับ)</label>
                        <input
                          type="text"
                          className="form-control"
                          placeholder="เบอร์ 10 หลัก หรือเลขบัตร 13 หลัก"
                          value={editPromptPay}
                          disabled={!promptPayFieldEnabled}
                          readOnly={!promptPayFieldEnabled}
                          onChange={(e) => setEditPromptPay(e.target.value.replace(/\D/g, '').slice(0, 13))}
                        />
                        <div className="htd-member-modal__hint">
                          {!promptPayFieldEnabled && canEditBank
                            ? 'กดปุ่มเพิ่มบัญชีธนาคารเพื่อกรอก PromptPay พร้อมกัน'
                            : allowBankEdit
                              ? 'กรอกเมื่อพร้อมเพย์ไม่ตรงกับเบอร์มือถือ ระบบจะบันทึกหลังเพิ่มบัญชีธนาคารสำเร็จ (บันทึกได้ครั้งเดียว)'
                              : 'กรอกเมื่อพร้อมเพย์ไม่ตรงกับเบอร์มือถือ (บันทึกได้ครั้งเดียว หลังจากนั้นต้องให้แอดมินแก้)'}
                        </div>
                      </div>
                    ) : !promptPayInputEnabled ? (
                      <div className="htd-member-modal__hint" style={{ marginTop: '0.75rem' }}>
                        ระบบปิดการให้ลูกค้ากรอก PromptPay เองสำหรับ tenant นี้ หากต้องการเพิ่มกรุณาติดต่อเจ้าหน้าที่
                      </div>
                    ) : null}
                  </div>

                  <div className="htd-member-modal__section htd-member-modal__section--accent">
                    <div className="htd-member-modal__section-head">
                      <div>
                        <h6>TrueMoney Wallet</h6>
                        <p>เพิ่มได้เฉพาะส่วนนี้โดยไม่กระทบข้อมูลเดิมส่วนอื่น</p>
                      </div>
                      <span className={`htd-member-modal__status${allowTrueWalletEdit ? ' is-open' : ''}`}>
                        {allowTrueWalletEdit
                          ? 'เพิ่มข้อมูลได้'
                          : canEditTrueWallet
                            ? 'เพิ่มได้จากปุ่ม TrueMoney'
                            : 'มีข้อมูลแล้ว'}
                      </span>
                    </div>
                    <div className="htd-member-modal__field">
                      <label>ชื่อ-นามสกุล (TrueMoney)</label>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="ชื่อที่ลงทะเบียน True Money"
                        value={editTrueName}
                        disabled={!allowTrueWalletEdit}
                        readOnly={!allowTrueWalletEdit}
                        onChange={(e) => setEditTrueName(e.target.value)}
                      />
                    </div>
                    <div className="htd-member-modal__grid">
                      <div className="htd-member-modal__field">
                        <label>เบอร์มือถือ</label>
                        <input
                          type="text"
                          className="form-control"
                          placeholder="10 หลัก"
                          value={editTrueMobile}
                          disabled={!allowTrueWalletEdit}
                          readOnly={!allowTrueWalletEdit}
                          onChange={(e) => setEditTrueMobile(e.target.value.replace(/\D/g, '').slice(0, 10))}
                        />
                      </div>
                      <div className="htd-member-modal__field">
                        <label>Wallet ID</label>
                        <input
                          type="text"
                          className="form-control"
                          placeholder="TrueMoney Wallet ID"
                          value={editTrueId}
                          disabled={!allowTrueWalletEdit}
                          readOnly={!allowTrueWalletEdit}
                          onChange={(e) => setEditTrueId(e.target.value)}
                        />
                      </div>
                    </div>
                    {!allowTrueWalletEdit && (
                      <div className="htd-member-modal__hint">
                        {canEditTrueWallet
                          ? 'รอบนี้ไม่ได้เปิดเพิ่ม TrueMoney ให้ใช้ปุ่มเพิ่มของส่วน TrueMoney โดยตรง'
                          : 'มีข้อมูล TrueMoney อยู่แล้ว ระบบจึงไม่เปิดให้แก้ไขทับข้อมูลเดิม'}
                      </div>
                    )}
                  </div>
                </div>

                <div className="htd-member-modal__footer">
                  <button
                    type="button"
                    className="htd-member-modal__btn htd-member-modal__btn--ghost"
                    onClick={() => setShowEditUserModal(false)}
                  >
                    ปิด
                  </button>
                  <button
                    type="button"
                    className="htd-member-modal__btn htd-member-modal__btn--primary"
                    disabled={submitting || !hasEditableSection}
                    onClick={handleSubmitEditProfile}
                  >
                    {submitting ? 'กำลังบันทึก...' : 'บันทึกข้อมูลใหม่'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  )
}

export default ModernMemberPage
